import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProjectApi, ProjectDTO } from './ProjectApi';
import { ProjectStatusOptions } from '../../constants/projectConstants';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const defaultProject: ProjectDTO = {
  id: '',
  tenantId: '',
  tenantName: '',
  contractId: '',
  contractBidId: '',
  licenseId: '',
  createdBy: '',
  updatedBy: '',
  projectName: '',
  description: '',
  projectType: '',
  status: 'DRAFT',
  contractBidNo: '',
  projectActualValue: 0,
  projectBidValue: 0,
  projectBidPercentage: 0,
  licenseHolderName: '',
  licenseNo: '',
  givenStartDate: '',
  givenEndDate: '',
  actualStartDate: '',
  actualEndDate: '',
  extensionCount: 0,
  extensionDate: '',
  terminationReason: '',
  comment: '',
};

export const ProjectForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [project, setProject] = useState<ProjectDTO>(defaultProject);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isReadOnly = mode === 'view';
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && id) {
      loadProjectById(id);
    } else if (mode === 'create') {
      if (location.state) {
        const contractData = location.state as Partial<ProjectDTO>;
        setProject((prev) => ({
          ...prev,
          ...contractData,
        }));
      } else {
        setProject(defaultProject);
      }
    }
  }, [id, mode, location.state]);

  const loadProjectById = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await ProjectApi.getById(projectId);
      setProject(response.data);
    } catch (err) {
      setError('Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    let updatedProject: ProjectDTO = { ...project, [name]: value };

    const actualValue = parseFloat(String(updatedProject.projectActualValue));
    const inputValue = parseFloat(value);

    if (name === 'projectBidPercentage' && !isNaN(actualValue) && !isNaN(inputValue)) {
      updatedProject.projectBidValue = actualValue + (actualValue * inputValue) / 100;
    }

    if (name === 'projectBidValue' && !isNaN(actualValue) && !isNaN(inputValue)) {
      updatedProject.projectBidPercentage = ((inputValue - actualValue) / actualValue) * 100;
    }

    setProject(updatedProject);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (mode === 'edit' && id) {
        await ProjectApi.update(id, project);
        alert('Project updated successfully!');
      } else if (mode === 'create') {
        await ProjectApi.create(project);
        alert('Project created successfully!');
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save project. Please try again.');
    }
  };

  const handleReset = () => {
    setProject(defaultProject);
  };

  const renderInput = (label: string, field: keyof ProjectDTO, type = 'text') => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={field}
        value={project[field] ?? ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  const renderStatusDropdown = () => (
    <div>
      <label className="block text-sm font-semibold mb-1">Status</label>
      <select
        name="status"
        value={project.status}
        onChange={handleChange}
        disabled={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      >
        {ProjectStatusOptions.map((status) => (
          <option key={status.value} value={status.value}>
            {status.label}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 mx-auto bg-white w-full shadow rounded max-w-7xl">
      {error && <div className="mb-4 text-red-600">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInput('Project Name', 'projectName')}
          {renderInput('Description', 'description')}
          {renderInput('Project Type', 'projectType')}
          {renderStatusDropdown()}
          {renderInput('Contract Bid No', 'contractBidNo')}
          {renderInput('Project Actual Value', 'projectActualValue', 'number')}
          {renderInput('Project Bid Value', 'projectBidValue', 'number')}
          {renderInput('Project Bid Percentage', 'projectBidPercentage', 'number')}
          {renderInput('Total Budget', 'totalBudget', 'number')}
          {renderInput('License Holder Name', 'licenseHolderName')}
          {renderInput('License No', 'licenseNo')}
          {renderInput('Given Start Date', 'givenStartDate', 'date')}
          {renderInput('Given End Date', 'givenEndDate', 'date')}
          {renderInput('Actual Start Date', 'actualStartDate', 'date')}
          {renderInput('Actual End Date', 'actualEndDate', 'date')}
          {renderInput('Extension Count', 'extensionCount', 'number')}
          {renderInput('Termination Reason', 'terminationReason')}
          {renderInput('Comment', 'comment')}
        </div>

        <div className="mt-6 flex gap-4">
          {mode !== 'view' && (
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              {mode === 'create' ? 'Create' : 'Update'}
            </button>
          )}

          {mode === 'create' && (
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};